import _, { add, hasIn } from "lodash";
import { v4 as uuidv4 } from "uuid";

/**
 * Initializes WPD Tree widget.
 */
function init(wpd, wpdDocument, dataEntryVM) {
	// overwrite the entire TreeWidget class to fix issues with the constructor
	// and add functionality
	wpd.TreeWidget = class {
		constructor($elem) {
			this.$mainElem = $elem;
			this.treeData = null;
			this.itemColors = {};
			this.$mainElem.removeEventListener("click", (e) =>
				this._onclick(e.target),
			);
			this.$mainElem.removeEventListener("keydown", (e) => this._onkeydown(e));
			this.$mainElem.removeEventListener("dblclick", (e) =>
				this._ondblclick(e),
			);
			this.$mainElem.addEventListener("click", (e) => {
				this._onclick(e.target);
			});
			this.$mainElem.addEventListener("keydown", (e) => this._onkeydown(e));
			this.$mainElem.addEventListener("dblclick", (e) => this._ondblclick(e));
			this.idmap = [];
			this.itemCount = 0;
			this.selectedPath = null;
		}

		// overwrite tree widget _renderFolder function to use id prefix
		_renderFolder(data, basePath, isInnerFolder) {
			if (data == null) {
				return;
			}

			const tags = dataEntryVM.getTags();
			const studyTag = _.find(tags, {
				name: wpd.CompositeKeyUtils.studyTag,
				isCompositeKeyMember: true,
			});
			const studyPartTag = _.find(tags, {
				name: wpd.CompositeKeyUtils.studyPartTag,
				isCompositeKeyMember: true,
			});
			const studyArmTag = _.find(tags, {
				name: wpd.CompositeKeyUtils.studyArmTag,
				isCompositeKeyMember: true,
			});
			const populationTag = _.find(tags, {
				name: wpd.CompositeKeyUtils.populationTag,
				isCompositeKeyMember: true,
			});
			const subgroupTag = _.find(tags, {
				name: wpd.CompositeKeyUtils.subgroupTag,
				isCompositeKeyMember: true,
			});

			let htmlStr = "";

			if (isInnerFolder) {
				htmlStr = '<ul class="tree-list">';
			} else {
				htmlStr = '<ul class="tree-list-root">';
			}

			const fileManager = wpd.appData.getFileManager();
			const datasetFileMap = fileManager.getDatasetNameMap();
			const currentFileIndex = fileManager.currentFileIndex();
			const fileDatasets = wpd.appData
				.getPlotData()
				.getDatasets()
				.filter((d) => datasetFileMap[d.name] === currentFileIndex);

			const pageManager = wpd.appData.getPageManager();
			const currentPageDatasets =
				pageManager && wpd.appData.isMultipage()
					? pageManager.getCurrentPageDatasets()
					: fileDatasets;

			const currentPageCompositeKeys = currentPageDatasets.flatMap(
				(dataset) => {
					const pixels = dataset.getAllPixels();
					return pixels
						.map((pixel) => pixel.metadata?.compositeKey)
						.filter((key) => !!key);
				},
			);

			const incompleteTables = new Set(
				currentPageDatasets
					.filter((dataset) => {
						const axes = wpd.appData.getPlotData().getAxesForDataset(dataset);

						if (!axes) {
							return false;
						}
						const metadata = axes.getMetadata();
						return (
							metadata.table &&
							!metadata.calibration?.cellTagGroup &&
							_.isEmpty(metadata.calibration?.cellTagGroups)
						);
					})
					.map((dataset) => {
						const axes = wpd.appData.getPlotData().getAxesForDataset(dataset);
						return axes.name;
					}),
			);

			for (let i = 0; i < data.length; i++) {
				const item = data[i];
				this.itemCount++;

				if (typeof item === "string") {
					htmlStr += `<li title="${item}">`;

					let itemPath = `${basePath}/${item}`;

					htmlStr += `<span class="tree-item" id="${this.prefix}tree-item-id-${this.itemCount}">${item}</span>`;

					this.idmap[this.itemCount] = itemPath;
				} else if (typeof item === "object") {
					const labelKey = Object.keys(item)[0];
					if (item[labelKey].type) {
						htmlStr += `<li class="axis-dataset-tree-list">`;
						htmlStr += '<div class="axis-dataset-tree-list-content">';
					} else {
						htmlStr += "<li>";
						htmlStr += '<div class="tree-list-content tree-list-content">';
					}

					let additionalClasses = "";
					if (item[labelKey].classes) {
						additionalClasses = ` ${item[labelKey].classes.join(" ")}`;
					}

					let dataAttributes = "";
					let isStudy = false;
					let isPart = false;
					let isArm = false;
					let isPopulation = false;
					let isSubgroup = false;
					let compositeKey = "";
					if (item[labelKey].data) {
						dataAttributes = item[labelKey].data
							.map(({ key, value }) => `data-${key}="${_.escape(value)}"`)
							.join(" ");

						const map = item[labelKey].data.reduce((acc, item) => {
							acc[item.key] = item.value;
							return acc;
						}, {});

						if (map.branch === "study") {
							isStudy = true;
						} else if (map.branch === "part") {
							isPart = true;
						} else if (map.branch === "arm") {
							isArm = true;
						} else if (map.branch === "population") {
							isPopulation = true;
						} else if (map.branch === "subgroup") {
							isSubgroup = true;
						}

						const compositeKeyObj = item[labelKey].data.find(
							(obj) => obj.key === "composite-key",
						);
						compositeKey = compositeKeyObj ? compositeKeyObj.value : "";
					}

					const isStudyTree =
						isStudy || isPart || isArm || isPopulation || isSubgroup;
					const isOnCurrentPage =
						!compositeKey ||
						currentPageCompositeKeys.some((key) =>
							wpd.CompositeKeyUtils.isChildKey(key, compositeKey),
						);
					const title =
						item[labelKey].compositeKey || item[labelKey].title || "";

					const hasIncompleteTable =
						item[labelKey].type !== "table-axis" &&
						labelKey === "Table" &&
						incompleteTables.size > 0;
					const isIncompleteTable =
						item[labelKey].type === "table-axis" &&
						incompleteTables.has(labelKey);
					const showWarning = hasIncompleteTable || isIncompleteTable;
					const warningText = "Table requires tag group selection";
					const color = item[labelKey].color;
					const colorIndicator =
						dataEntryVM.pointGroupColors && color
							? `<span class="color-indicator" style="display: inline-block; width: 10px; height: 10px; border-radius: 50%; background-color: ${color}; margin-right: 6px; border: 1px solid #ccc;"></span>`
							: "";
					htmlStr += `
						<div class="tree-folder-container">
							<div
								class="tree-folder ${
									isOnCurrentPage ? "" : "tree-off-page"
								}${additionalClasses}"
								id="${this.prefix}tree-item-id-${this.itemCount}"
								title="${showWarning ? _.escape(warningText) : _.escape(title)}"
								${dataAttributes}
								${showWarning ? "missing-tag-group" : ""}
							>
								${
									showWarning
										? `<span title="${warningText}" style="font-size: 1em;">&#x26A0;&#xFE0F;</span>`
										: ``
								} ${colorIndicator}<span class="tree-folder-label">${labelKey}</span>
							</div>
					`;

					if (isOnCurrentPage && isStudyTree) {
						let currentTag;
						if (isStudy) {
							currentTag = studyTag;
						} else if (isPart) {
							currentTag = studyPartTag;
						} else if (isArm) {
							currentTag = studyArmTag;
						} else if (isPopulation) {
							currentTag = populationTag;
						} else {
							currentTag = subgroupTag;
						}

						if (!dataEntryVM.readOnly) {
							htmlStr += `
								<input
									data-tag-uuid="${currentTag.uuid}"
									type="button"
									value="✎"
									class="edit-button study-row-button collected"
									onclick="wpd.tree.editAbbreviation(this, event);"
									title="Edit study"
								/>`;
						}
					}

					if (
						item[labelKey].type &&
						item[labelKey].type.endsWith("-axis") &&
						!dataEntryVM.readOnly
					) {
						let title = "Rename";
						if (
							item[labelKey].type === "xy-axis" ||
							item[labelKey].type === "bar-axis"
						) {
							title = "Rename Graph";
						} else if (item[labelKey].type === "table-axis") {
							title = "Rename Table";
						}

						htmlStr += `
							<input
								type="button"
								value="✎"
								title="${title}"
								class="edit-button"
								onclick="wpd.tree.renameAxis(${wpd.utils.escapeForJS(labelKey)});"
							/>
						`;
					}

					htmlStr += `</div>`;
					htmlStr += `<div class="study-row-button-container">`;

					if (isStudy) {
						htmlStr += this._studyButtons(
							isOnCurrentPage,
							studyTag,
							studyPartTag,
							studyArmTag,
						);
					} else if (isPart) {
						htmlStr += this._studyPartButtons(isOnCurrentPage, studyArmTag);
					} else if (isArm) {
						htmlStr += this._studyArmButtons(
							isOnCurrentPage,
							studyArmTag,
							populationTag,
							subgroupTag,
						);
					} else if (isPopulation) {
						htmlStr += this._studyPopulationButtons(
							isOnCurrentPage,
							populationTag,
							subgroupTag,
						);
					} else if (isSubgroup) {
						htmlStr += this._studySubgroupButtons(isOnCurrentPage, subgroupTag);
					}

					htmlStr += `</div>`;

					// add axis edit buttons
					if (item[labelKey].type && item[labelKey].type !== "image-axis") {
						const map = {
							"bar-axis": "bar graph",
							"bar-dataset": "bar graph",
							"table-axis": "table",
							"table-dataset": "table",
							"xy-axis": "XY-graph",
							"xy-dataset": "XY-graph",
						};

						if (
							!dataEntryVM.readOnly &&
							item[labelKey].type.endsWith("-axis")
						) {
							// axes have edit and rename buttons
							// for xy-axes, in addition to those, it has an edit label button
							htmlStr += '<div class="axis-dataset-tree-row">';

							// edit axis button
							htmlStr += `
								<input
									type="button"
									value="⚙"
									title="Edit ${map[item[labelKey].type]} axis"
									class="axis-dataset-tree-button"
									style="padding-top: 0px;"
									onclick="wpd.tree.editAxis(${wpd.utils.escapeForJS(labelKey)}, this);"
									${showWarning ? "missing-tag-group" : ""}
								/>
							`;

							// delete axis button
							htmlStr += `
								<input
									type="button"
									value="⌫"
									title="Delete ${map[item[labelKey].type]} axis"
									class="axis-dataset-tree-button"
									style="font-size: 10px; color: red;"
									onclick="wpd.tree.deleteAxis(${wpd.utils.escapeForJS(labelKey)});"
								/>
							`;

							htmlStr += "</div>";
						} else if (
							!dataEntryVM.readOnly &&
							item[labelKey].type.endsWith("-dataset")
						) {
							// datasets have edit and rename buttons

							htmlStr += '<div class="axis-dataset-tree-row">';

							if (
								item[labelKey].type === "xy-dataset" ||
								item[labelKey].type === "bar-dataset"
							) {
								htmlStr += `
									<input
										type="button"
										value="⚙"
										title="Edit ${map[item[labelKey].type]} study branch"
										class="axis-dataset-tree-button"
										style="padding-top: 0px;"
										onclick="wpd.tree.editDataset(${wpd.utils.escapeForJS(item[labelKey].name)});"
									/>
								`;
							}

							// delete dataset button
							htmlStr += `
								<input
									type="button"
									value="⌫"
									title="Delete ${map[item[labelKey].type]} dataset"
									class="axis-dataset-tree-button"
									style="font-size: 10px; color: red;"
									onclick="wpd.tree.deleteDataset(${wpd.utils.escapeForJS(item[labelKey].name)});"
								/>
							`;

							htmlStr += "</div>";
						}
					}

					htmlStr += "</div>";
					htmlStr += `<hr style="color: #dddddd; height: 1px; margin: 1px 0 1px 0;">`;

					// escape slashes so the selection logic continues to work
					const path = wpd.utils.escapeSlashes(item[labelKey].name || labelKey);

					this.idmap[this.itemCount] = isStudyTree
						? compositeKey
						: `${basePath}/${path}`;

					htmlStr += this._renderFolder(
						item[labelKey].children,
						this.idmap[this.itemCount],
						true,
					);
				}

				htmlStr += "</li>";
			}

			htmlStr += "</ul>";

			return htmlStr;
		}

		_studyButtons(isOnCurrentPage, studyTag, studyPartTag, studyArmTag) {
			if (dataEntryVM.readOnly) {
				return "";
			}

			return `
				<input
					data-tag-uuid="${studyPartTag.uuid}"
					type="button"
					value="P"
					class="create-study-part-button study-row-button"
					onclick="wpd.tree.addNewStudyPart(this, event);"
					title="Add study part"
				/>
				<input
					data-tag-uuid="${studyArmTag.uuid}"
					type="button"
					value="A"
					class="create-study-arm-button study-row-button"
					onclick="wpd.tree.addNewStudyArm(this, event);"
					title="Add study arm"
				/>
				${
					isOnCurrentPage
						? `
						<input
							type="button"
							value="⌫"
							class="delete-study-button study-row-button"
							onclick="wpd.tree.showDeleteStudyElementPopup(this, event);"
							title="Delete study"
						/>`
						: ``
				}
			`;
		}

		_studyPartButtons(isOnCurrentPage, studyArmTag) {
			if (dataEntryVM.readOnly) {
				return "";
			}

			return `
				<input
					data-tag-uuid="${studyArmTag.uuid}"
					type="button"
					value="A"
					class="create-study-arm-button study-row-button"
					onclick="wpd.tree.addNewStudyArm(this, event);"
					title="Add study arm"
				/>
				${
					isOnCurrentPage
						? `
						<input
							type="button"
							value="⌫"
							class="delete-study-part-button study-row-button"
							onclick="wpd.tree.showDeleteStudyElementPopup(this, event);"
							title="Delete study part"
						/>`
						: ``
				}
			`;
		}

		_studyArmButtons(isOnCurrentPage, studyArmTag, populationTag, subgroupTag) {
			if (dataEntryVM.readOnly) {
				return "";
			}

			return `
				<input
					data-tag-uuid="${populationTag.uuid}"
					type="button"
					value="pop"
					class="create-population-button study-row-button"
					onclick="wpd.tree.addNewPopulation(this, event);"
					title="Add population"
				/>
				<input
					data-tag-uuid="${subgroupTag.uuid}"
					type="button"
					value="sub"
					class="create-subgroup-button study-row-button"
					onclick="wpd.tree.addNewSubgroup(this, event);"
					title="Add subgroup"
				/>
				${
					isOnCurrentPage
						? `
						<input
							type="button"
							value="⌫"
							class="delete-study-arm-button study-row-button"
							onclick="wpd.tree.showDeleteStudyElementPopup(this, event);"
							title="Delete study arm"
						/>`
						: ``
				}
			`;
		}

		_studyPopulationButtons(isOnCurrentPage, populationTag, subgroupTag) {
			if (dataEntryVM.readOnly) {
				return "";
			}

			return `
				<input
					data-tag-uuid="${subgroupTag.uuid}"
					type="button"
					value="sub"
					class="create-subgroup-button study-row-button"
					onclick="wpd.tree.addNewSubgroup(this, event);"
					title="Add subgroup"
				/>
				${
					isOnCurrentPage
						? `
						<input
							type="button"
							value="⌫"
							class="delete-study-arm-button study-row-button"
							onclick="wpd.tree.showDeleteStudyElementPopup(this, event);"
							title="Delete study population"
						/>`
						: ``
				}
			`;
		}

		_studySubgroupButtons(isOnCurrentPage, subgroupTag) {
			if (dataEntryVM.readOnly) {
				return "";
			}

			return `
				${
					isOnCurrentPage
						? `
						<input
							type="button"
							value="⌫"
							class="delete-study-arm-button study-row-button"
							onclick="wpd.tree.showDeleteStudyElementPopup(this, event);"
							title="Delete study subgroup"
						/>`
						: ``
				}
			`;
		}

		// Expected format:
		// treeData = ["item0", {"folder0": ["sub-item0", "sub-item1"]}, "item1"]
		// itemColors = {"path0" : wpd.Color, "path1" : wpd.Color}
		// overwrite tree widget render function to include id prefix
		render(treeData, itemColors, prefix = "") {
			this.idmap = [];
			this.itemCount = 0;
			this.treeData = treeData;
			this.itemColors = itemColors;
			this.prefix = prefix;

			this.$mainElem.innerHTML = this._renderFolder(this.treeData, "", false);

			this._addStudyButton();

			this.selectedPath = null;
		}

		_addStudyButton() {
			const studyList = wpdDocument.getElementsByClassName("study-list")[0];

			const addStudyButton = wpdDocument.getElementsByClassName(
				"create-study-button",
			)[0];

			if (studyList && !addStudyButton) {
				const tags = dataEntryVM.getTags();
				const studyKeyTag = _.find(tags, {
					name: wpd.CompositeKeyUtils.studyTag,
				});

				studyList.insertAdjacentHTML(
					"afterbegin",
					`
					<input
						data-tag-uuid="${studyKeyTag.uuid}"
						type="button"
						value='Add study'
						title="Create study"
						class="create-study-button ${dataEntryVM.readOnly ? "button-disabled" : ""}"
						onclick="wpd.tree.addNewStudy(this);"
					/>`,
				);
			}
		}

		_unselectAll() {
			const $folders = this.$mainElem.querySelectorAll(".tree-folder");
			const $items = this.$mainElem.querySelectorAll(".tree-item");

			$folders.forEach(function ($e) {
				$e.classList.remove("tree-selected");
			});
			$items.forEach(function ($e) {
				$e.classList.remove("tree-selected");
			});
			this.selectedPath = null;
		}

		// overwrite tree widget selectPath function to use id prefix
		async selectPath(
			itemPath,
			suppressSecondaryActions,
			preserveActive,
			ignoreCallback,
			selectNextInstance,
		) {
			const itemId = this.idmap.indexOf(itemPath);
			if (itemId >= 0) {
				const previousPath = this.selectedPath;
				this._unselectAll();
				this.selectedPath = itemPath;
				const $item = wpdDocument.getElementById(
					`${this.prefix}tree-item-id-${itemId}`,
				);
				$item.classList.add("tree-selected");

				if (!ignoreCallback && this.itemSelectionCallback != null) {
					this.itemSelectionCallback(
						$item,
						itemPath,
						suppressSecondaryActions,
						preserveActive,
						previousPath,
						selectNextInstance,
					);
				}
			}
		}

		// overwrite tree widget _onclick function to use id prefix
		async _onclick(target) {
			// e.target is whatever the user clicked - could be the row div itself,
			// the wrapping label span, a filter-highlight <b>, or the warning emoji
			// span. Resolve up to the actual row before reading id / classes / data
			// attributes (consumers like axisTree.onItemSelection read data-* off el).
			const row = target.closest?.(".tree-item, .tree-folder") ?? null;
			if (!row) return;

			const isItem = row.classList.contains("tree-item");
			const isFolder = row.classList.contains("tree-folder");
			const isDisabled = row.classList.contains("tree-disabled");

			if (!isDisabled && (isItem || isFolder)) {
				const previousPath = this.selectedPath;
				this._unselectAll();
				row.classList.add("tree-selected");
				if (this.itemSelectionCallback != null) {
					const itemId = parseInt(
						row.id.replace(`${this.prefix}tree-item-id-`, ""),
						10,
					);

					if (!isNaN(itemId)) {
						this.selectedPath = this.idmap[itemId];
						await this.itemSelectionCallback(
							row,
							this.idmap[itemId],
							false,
							false,
							previousPath,
							true,
						);
					}
				}
			}
		}

		_onkeydown(e) {
			// allow either F2 or Meta+R to trigger rename
			if (e.key === "F2" || (e.key.toLowerCase() === "r" && e.metaKey)) {
				if (this.itemRenameCallback) {
					const row =
						e.target.closest?.(".tree-item, .tree-folder") ?? e.target;
					this.itemRenameCallback(row, this.selectedPath, false);
					e.preventDefault();
				}
			}
		}

		_ondblclick(e) {
			if (this.itemRenameCallback) {
				const row = e.target.closest?.(".tree-item, .tree-folder") ?? e.target;
				this.itemRenameCallback(row, this.selectedPath, false);
				e.preventDefault();
				e.stopPropagation();
			}
		}

		onItemSelection(callback) {
			this.itemSelectionCallback = callback;
		}

		onItemRename(callback) {
			this.itemRenameCallback = callback;
		}

		getSelectedPath() {
			return this.selectedPath;
		}
	};

	// overwrite root tree
	wpd.tree = (function () {
		// tree widgets
		let rootTree = null;
		let axisTree = null;

		// current axis and dataset
		let activeAxis = null;
		let activeDataset = null;
		let activePointGroup = null;

		// store information for the wizard
		let wizard = false;
		let wizardDatasets = [];

		const mode = {
			text: "Text",
			graph: "Graph",
			table: "Table",
		};

		const mainDataTypeDisplayName = "Central tendency";

		// polyfill for IE11/Microsoft Edge
		if (window.NodeList && !NodeList.prototype.forEach) {
			NodeList.prototype.forEach = function (callback, thisArg) {
				thisArg = thisArg || window;
				for (let i = 0; i < this.length; i++) {
					callback.call(thisArg, this[i], i, this);
				}
			};
		}

		function textPath() {
			return `/${mode.text}`;
		}

		function graphPath() {
			return `/${mode.graph}`;
		}

		function tablePath() {
			return `/${mode.table}`;
		}

		function buildTree() {
			// if rootTree is null something is terribly wrong, short-circuit
			if (rootTree == null) {
				return;
			}

			const itemColors = {};
			const treeData = [
				{
					[mode.text]: {
						// TODO: tree-disabled?
						classes: [],
						title: `${mode.text} data`,
						children: [],
					},
				},
				{
					[mode.graph]: {
						title: `${mode.graph} data`,
						children: [],
					},
				},
				{
					[mode.table]: {
						title: `${mode.table} data`,
						children: [],
					},
				},
			];

			rootTree.render(treeData, itemColors);

			showTreeItemWidget(null);
		}

		function getRootTreeSelectedPath() {
			if (rootTree) {
				return rootTree.getSelectedPath();
			}

			return null;
		}

		function showTreeItemWidget(id) {
			const $rootTrees = wpdDocument.querySelectorAll(".tree-widget");
			$rootTrees.forEach(function ($e) {
				if ($e.id === id) {
					$e.style.display = "inline";
				} else {
					$e.style.display = "none";
				}
			});
		}

		function resetGraphics() {
			wpd.graphicsWidget.removeTool();
			wpd.graphicsWidget.resetData();
			wpd.sidebar.clear();
		}

		function setActivePointGroup(pointGroup) {
			if (pointGroup) {
				activePointGroup = pointGroup;
			} else {
				activePointGroup = null;
			}
		}

		function getActivePointGroup() {
			return activePointGroup;
		}

		function onDatasetSelection(datasetName, pointGroupData) {
			// get dataset
			const plotData = wpd.appData.getPlotData();
			const datasets = plotData.getDatasets();
			const dataset = datasets.find((d) => d.name === datasetName);

			if (dataset) {
				// clean up existing UI
				resetGraphics();

				wpd.tree.setActiveDataset(dataset);

				if (
					pointGroupData &&
					(pointGroupData.isCentralTendency ||
						pointGroupData.tag?.name !== mainDataTypeDisplayName)
				) {
					wpd.tree.setActivePointGroup(pointGroupData);
				} else {
					// main data type active
					wpd.tree.setActivePointGroup();
				}

				// set up UI for the new dataset
				wpd.acquireData.load();

				const axis = plotData.getAxesForDataset(activeDataset);
				const axisType = wpd.custom.identifyAxis(axis);
				if (["xy", "bar"].includes(axisType)) {
					wpdDocument
						.getElementById("manual-select-button")
						.classList.remove("button-disabled");
				}
			}

			// dispatch dataset select event
			wpd.events.dispatch("wpd.dataset.select", {
				dataset: activeDataset,
			});
		}

		function onDatasetGroupSelection() {
			let axesList = [];
			let datasetList = [];

			const plotData = wpd.appData.getPlotData();
			const fileManager = wpd.appData.getFileManager();
			const datasetFileMap = fileManager.getDatasetNameMap();
			const currentFileIndex = fileManager.currentFileIndex();

			let datasets = plotData
				.getDatasets()
				.filter((d) => datasetFileMap[d.name] === currentFileIndex);

			if (wpd.appData.isMultipage()) {
				const pageManager = wpd.appData.getPageManager();
				const currentPage = pageManager.currentPage();
				const datasetPageMap = pageManager.getDatasetNameMap();
				for (let ds of datasets.filter(
					(d) => datasetPageMap[d.name] === currentPage,
				)) {
					axesList.push(plotData.getAxesForDataset(ds));
					datasetList.push(ds);
				}
			} else {
				for (let ds of datasets) {
					axesList.push(plotData.getAxesForDataset(ds));
					datasetList.push(ds);
				}
			}
			wpd.tree.setActivePointGroup(null);
			wpd.graphicsWidget.setRepainter(
				new wpd.MultipltDatasetRepainter(axesList, datasetList),
			);
		}

		function onTextSelection() {
			let axis = null;
			let dataset = null;

			const plotData = wpd.appData.getPlotData();
			const fileManager = wpd.appData.getFileManager();

			const datasetFileMap = fileManager.getDatasetNameMap();
			const currentFileIndex = fileManager.currentFileIndex();

			// filter datasets associated with the current file
			const fileDatasets = plotData
				.getDatasets()
				.filter((d) => datasetFileMap[d.name] === currentFileIndex);

			let datasets = fileDatasets;

			// filter datasets down to current page, if necessary
			let currentPage = null;
			if (wpd.appData.isMultipage()) {
				const pageManager = wpd.appData.getPageManager();
				currentPage = pageManager.currentPage();
				const datasetPageMap = pageManager.getDatasetNameMap();

				// only include datasets associated with the current file and page
				// loop through datasets associated with the current file and page
				datasets = fileDatasets.filter(
					(d) => datasetPageMap[d.name] === currentPage,
				);
			}

			// loop through datasets associated with the current file
			for (let d of datasets) {
				// find the image axis that is not a table axis and not an xy label axis
				// NOTE: there should only be one image axis type per file (per page), except for xy labels
				const a = plotData.getAxesForDataset(d);

				if (
					a instanceof wpd.ImageAxes &&
					!a.getMetadata().table &&
					!a.name.endsWith("Labels")
				) {
					axis = a;
					dataset = d;
				}
			}

			// if axis and dataset cannot be found, create a new one
			if (!axis && !dataset) {
				// create the new axis
				const newAxis = new wpd.ImageAxes();
				let axisName = "Text";
				if (currentPage !== null) {
					axisName = `Page ${currentPage} text`;
				}
				newAxis.name = wpd.custom.makeAxisName(axisName);
				newAxis.calibrate();
				// add new axis to the collection
				plotData.addAxes(newAxis, wpd.appData.isMultipage());
				fileManager.addAxesToCurrentFile([newAxis]);
				if (wpd.appData.isMultipage()) {
					wpd.appData.getPageManager().addAxesToCurrentPage([newAxis]);
				}
				// set newly created axis as active axis
				wpd.tree.setActiveAxis(newAxis);
				// dispatch axes add event
				wpd.events.dispatch("wpd.axes.add", {
					axes: newAxis,
				});
				// make a new uuid
				const uuid = uuidv4();
				// define default text dataset name
				const name = "Text Data";
				// create the new dataset associated with the axis
				const newDataset = new wpd.Dataset();
				newDataset.name = `${name}_${uuid}`;
				newDataset.setMetadata({
					name: name,
					uuid: uuid,
				});
				// add new dataset to collections
				plotData._datasetColl.push(newDataset);
				plotData.setAxesForDataset(newDataset, newAxis);
				fileManager.addDatasetsToCurrentFile([newDataset]);
				if (wpd.appData.isMultipage()) {
					wpd.appData.getPageManager().addDatasetsToCurrentPage([newDataset]);
				}
				// set newly created dataset as active dataset
				wpd.tree.setActiveDataset(newDataset);
				// dispatch dataset add event
				wpd.events.dispatch("wpd.dataset.add", {
					dataset: newDataset,
				});
			} else {
				// set active axis and dataset
				wpd.tree.setActiveAxis(axis);
				wpd.tree.setActiveDataset(dataset);
			}

			// dispatch axis and dataset selection events
			wpd.events.dispatch("wpd.axes.select", {
				axes: activeAxis,
			});

			wpd.events.dispatch("wpd.dataset.select", {
				dataset: activeDataset,
			});

			// load tools
			wpd.acquireData.load();

			// unset active point group
			wpd.tree.setActivePointGroup(null);

			setTimeout(() => wpd.acquireData.adjustPoints(), 0);

			// initialize tag group select
			wpd.tagGroups.showControls();
		}

		function onGraphSelection(preserveActive) {
			const axisList = [];
			const datasetList = [];

			const labelAxisList = [];
			const labelDatasetList = [];

			const plotData = wpd.appData.getPlotData();
			const fileManager = wpd.appData.getFileManager();

			const datasetFileMap = fileManager.getDatasetNameMap();
			const currentFileIndex = fileManager.currentFileIndex();

			// filter datasets associated with the current file
			const fileDatasets = plotData
				.getDatasets()
				.filter((d) => datasetFileMap[d.name] === currentFileIndex);

			let datasets = fileDatasets;

			// filter datasets down to current page, if necessary
			if (wpd.appData.isMultipage()) {
				const pageManager = wpd.appData.getPageManager();
				const currentPage = pageManager.currentPage();
				const datasetPageMap = pageManager.getDatasetNameMap();

				// only include datasets associated with the current file and page
				datasets = fileDatasets.filter(
					(d) => datasetPageMap[d.name] === currentPage,
				);
			}

			// loop through datasets associated with the current file
			for (let dataset of datasets) {
				const axis = plotData.getAxesForDataset(dataset);

				// collect non-image axes
				if (!(axis instanceof wpd.ImageAxes)) {
					axisList.push(axis);
					datasetList.push(dataset);
				} else {
					if (axis.name.endsWith("Labels")) {
						labelAxisList.push(axis);
						labelDatasetList.push(dataset);
					}
				}
			}

			if (dataEntryVM.reworkXYGraphCalibration) {
				const allAxes = plotData.getAxesColl();
				allAxes.forEach((a) => {
					const axisType = wpd.custom.identifyAxis(a);
					if (axisType === "xy") {
						axisList.push(a);
					}
				});
			}

			// generate graph tree
			const graphTreeContainer = wpdDocument.getElementsByClassName(
				"tree-widget-graphs-list",
			)[0];

			// clone and replace the container to remove all previous listeners
			const clone = graphTreeContainer.cloneNode();
			graphTreeContainer.parentNode.replaceChild(clone, graphTreeContainer);

			axisTree = new wpd.TreeWidget(clone);
			const graphAxes = [...new Set(axisList)];

			// setup graph tree event handlers
			axisTree.onItemSelection((el, path) => {
				// unselect all data points
				activeDataset?.unselectAll();

				let axisName = el.getAttribute("data-axis-name");
				let datasetName = el.getAttribute("data-dataset-name");
				let pointGroupName = el.getAttribute("data-point-group-name");

				// axis selected
				if (axisName) {
					// find the axis
					const graphAxis = graphAxes.find((axis) => axis.name === axisName);

					// dataset selected
					if (datasetName) {
						// unescape dataset name with slashes
						datasetName = wpd.utils.unescapeSlashes(datasetName);

						const pointGroupTagName = el.getAttribute(
							"data-point-group-tag-name",
						);
						const pointGroupAxis = el.getAttribute("data-point-group-axis");
						let pointGroupData;
						if (pointGroupName && pointGroupName !== mainDataTypeDisplayName) {
							if (
								dataEntryVM.pointGroupColors &&
								pointGroupTagName &&
								pointGroupAxis
							) {
								// axis point groups are keyed by direction and the tag
								// objects carry no `axis` field, so scope the lookup by
								// the direction key (DCTL-1772).
								const pointGroupTag = wpd.pointGroups.getAxisPointGroupTag(
									graphAxis,
									pointGroupAxis,
									pointGroupTagName,
								);

								pointGroupData = {
									tag: pointGroupTag,
									axisDirection: pointGroupAxis,
								};
							} else {
								pointGroupData = wpd.pointGroups.getPointGroupFromDisplayName(
									graphAxis,
									pointGroupName,
								);
							}
						} else if (
							pointGroupName === mainDataTypeDisplayName &&
							dataEntryVM.readOnly
						) {
							// In read-only mode, clicking "Central tendency" isolates main
							// points only via a sentinel handled by DataPointsRepainter.
							pointGroupData = { isCentralTendency: true };
						}
						// load the tools to add points to the dataset
						onDatasetSelection(datasetName, pointGroupData);
					} else {
						// no dataset within the axis selected
						// get all datasets associated with the selected axis
						const datasets = plotData.getDatasetsForAxis(graphAxis);

						// get the label axis and dataset
						const labelAxis = labelAxisList.find(
							(axis) => axis.name === `${graphAxis.name} Labels`,
						);
						const labelDataset = labelDatasetList.find(
							(dataset) => dataset.name === `${graphAxis.name} Labels`,
						);

						// clean up existing UI
						resetGraphics();
						wpd.tree.setActivePointGroup(null);

						const paintArrays = Array(datasets.length).fill(graphAxis);
						const paintDatasets = datasets;

						// only include label axis and dataset data if they exist
						if (labelAxis) {
							paintArrays.push(labelAxis);
						}
						if (labelDataset) {
							paintDatasets.push(labelDataset);
						}

						// paint all the datasets associated with the axis
						wpd.graphicsWidget.setRepainter(
							new wpd.MultipltDatasetRepainter(paintArrays, paintDatasets),
						);

						if (dataEntryVM.reworkXYGraphCalibration) {
							wpd.tree.setActiveAxis(graphAxis);
							wpd.events.dispatch("wpd.axes.select", {
								axes: graphAxis,
							});
						}
					}
				}
			});

			// subfunctions to generate dataset tree within axis tree
			const generateDatasetTreeData = (axis, axisType) => {
				// get the tags and transform it into a map (uuid : name)
				const tags = dataEntryVM.getTags();
				const tagMap = _.zipObject(
					_.map(tags, "uuid"),
					_.chain(tags).map("name").map(_.trim).value(),
				);

				const mainTags = wpd.alignAxes.getGraphAxisTags(axis);

				const pointGroupColors =
					wpd.pointGroups.calculatePointGroupColors(axis);

				// Convert Maps to objects for detailed logging
				const axisTypeMap = {
					bar: (d) => {
						let displayName = d.name;

						const metadata = d.getMetadata();
						if (metadata.name) {
							displayName = metadata.name;
						}

						const pointGroupsV2 = metadata.pointGroups;
						const pointGroups =
							dataEntryVM.pointGroupColors && pointGroupsV2
								? pointGroupsV2
								: d.getPointGroups().map((uuid) => {
										return { tagUuid: uuid };
								  });

						return {
							[displayName]: {
								compositeKey: metadata.studyData.compositeKey,
								title: `${axisType} dataset`,
								type: `${axisType}-dataset`,
								name: d.name,
								data: [
									{
										key: "axis-name",
										value: axis.name,
									},
									{
										key: "dataset-name",
										value: d.name,
									},
								],
								children: pointGroups.map((pointGroup, index) => {
									const uuid = pointGroup.tagUuid;
									let pointGroupDisplayName = tagMap[uuid];
									let pointGroupTitle = `${displayName} point group`;
									let pointGroupColor = null;

									const { barMainTag } = mainTags;

									const isMainDataType = barMainTag.uuid === uuid;

									let axisDirection = null;
									if (isMainDataType) {
										pointGroupDisplayName = `${mainDataTypeDisplayName}`;
										pointGroupTitle = `${pointGroupTitle}`;
										pointGroupTitle += `\n${barMainTag.name}`;
										if (dataEntryVM.pointGroupColors) {
											pointGroupColor =
												pointGroupColors.y.get(0) ?? pointGroupColors.x.get(0);
										}
									} else {
										axisDirection =
											pointGroup.axis ??
											wpd.pointGroups.findAxisDirectionForTag(axis, { uuid });

										if (dataEntryVM.pointGroupColors) {
											pointGroupColor =
												axisDirection === "y"
													? pointGroupColors.y.get(index)
													: pointGroupColors.x.get(index);
										}

										if (axisDirection) {
											pointGroupDisplayName = `[${axisDirection.toUpperCase()}] ${
												tagMap[uuid]
											}`;
										}
									}

									return {
										[pointGroupDisplayName]: {
											title: pointGroupTitle,
											type: `${displayName}-point-group`,
											color: pointGroupColor,
											children: [],
											data: [
												{
													key: "axis-name",
													value: axis.name,
												},
												{
													key: "dataset-name",
													value: d.name,
												},
												{
													key: "point-group-name",
													value: pointGroupDisplayName,
												},
												{
													key: "point-group-tag-name",
													value: tagMap[uuid],
												},
												{
													key: "point-group-axis",
													value: axisDirection,
												},
											],
										},
									};
								}),
							},
						};
					},
					xy: (d) => {
						let displayName = d.name;

						const metadata = d.getMetadata();
						if (metadata.name) {
							displayName = metadata.name;
						}

						const pointGroupsV2 = metadata.pointGroups;
						const pointGroups =
							dataEntryVM.pointGroupColors && pointGroupsV2
								? pointGroupsV2
								: d.getPointGroups().map((uuid) => {
										return { tagUuid: uuid };
								  });

						let filteredPointGroups = [...pointGroups];
						if (dataEntryVM.pointGroupOverhaul) {
							// check if there are more than 1 central tendencies
							// if so, remove one of them so only one is displayed
							const mainPointGroups = pointGroups.filter(
								(pointGroup) => pointGroup.center,
							);
							if (mainPointGroups.length > 1) {
								// find the last point group with center key
								const lastIndex = pointGroups
									.map((pointGroup, index) => (pointGroup.center ? index : -1))
									.filter((index) => index !== -1)
									.pop();

								// remove it
								if (lastIndex !== undefined) {
									filteredPointGroups.splice(lastIndex, 1);
								}
							}
						}

						return {
							[displayName]: {
								compositeKey: metadata.studyData.compositeKey,
								title: `${axisType} dataset`,
								type: `${axisType}-dataset`,
								name: d.name,
								data: [
									{
										key: "axis-name",
										value: axis.name,
									},
									{
										key: "dataset-name",
										value: d.name,
									},
								],
								// graph dataset point groups
								children: filteredPointGroups.map((pointGroup, index) => {
									const uuid = pointGroup.tagUuid;
									let pointGroupDisplayName = tagMap[uuid];
									let pointGroupTitle = `${displayName} point group`;
									let pointGroupColor = null;

									const { xMainTag, yMainTag } = mainTags;

									const isMainDataType = [
										xMainTag.uuid,
										yMainTag.uuid,
									].includes(uuid);

									let axisDirection = null;
									if (isMainDataType) {
										pointGroupDisplayName = `${mainDataTypeDisplayName}`;
										pointGroupTitle = `${pointGroupTitle}`;
										pointGroupTitle += `\n[X]: ${xMainTag.name}`;
										pointGroupTitle += `\n[Y]: ${yMainTag.name}`;
										if (dataEntryVM.pointGroupColors) {
											pointGroupColor =
												pointGroupColors.y.get(0) ?? pointGroupColors.x.get(0);
										}
									} else {
										axisDirection =
											pointGroup.axis ??
											wpd.pointGroups.findAxisDirectionForTag(axis, { uuid });

										if (dataEntryVM.pointGroupColors) {
											pointGroupColor =
												axisDirection === "y"
													? pointGroupColors.y.get(index)
													: pointGroupColors.x.get(index);
										}

										if (axisDirection) {
											pointGroupDisplayName = `[${axisDirection.toUpperCase()}] ${
												tagMap[uuid]
											}`;
										}
									}

									return {
										[pointGroupDisplayName]: {
											title: pointGroupTitle,
											type: `${displayName}-point-group`,
											color: pointGroupColor,
											children: [],
											data: [
												{
													key: "axis-name",
													value: axis.name,
												},
												{
													key: "dataset-name",
													value: d.name,
												},
												{
													key: "point-group-name",
													value: pointGroupDisplayName,
												},
												{
													key: "point-group-tag-name",
													value: tagMap[uuid],
												},
												{
													key: "point-group-axis",
													value: axisDirection,
												},
											],
										},
									};
								}),
							},
						};
					},
				};

				return datasetList
					.filter((d) => plotData.getAxesForDataset(d) === axis)
					.map(axisTypeMap[axisType]);
			};

			// generate the axis tree
			const axisTreeData = graphAxes.map((axis) => {
				const axisType = wpd.custom.identifyAxis(axis);

				// graph axis
				return {
					[axis.name]: {
						title: `${axisType} axis`,
						type: `${axisType}-axis`,
						// graph datasets
						children: generateDatasetTreeData(axis, axisType),
						data: [
							{
								key: "axis-name",
								value: axis.name,
							},
						],
					},
				};
			});

			// render the graph tree
			axisTree.render(axisTreeData, null, "graph-axis-");

			// set active axis to null
			if (!preserveActive) {
				wpd.tree.setActiveAxis(null);

				wpd.tagGroups.hideControls();
			}

			// clean up existing UI
			resetGraphics();

			// paint all the datasets
			wpd.graphicsWidget.setRepainter(
				new wpd.MultipltDatasetRepainter(
					[...axisList, ...labelAxisList],
					[...datasetList, ...labelDatasetList],
				),
			);
		}

		function onTableSelection() {
			const axisList = [];
			const datasetList = [];

			const plotData = wpd.appData.getPlotData();
			const fileManager = wpd.appData.getFileManager();

			const datasetFileMap = fileManager.getDatasetNameMap();
			const currentFileIndex = fileManager.currentFileIndex();

			// filter datasets associated with the current file
			const fileDatasets = plotData
				.getDatasets()
				.filter((d) => datasetFileMap[d.name] === currentFileIndex);

			let datasets = fileDatasets;

			// filter datasets down to current page, if necessary
			if (wpd.appData.isMultipage()) {
				const pageManager = wpd.appData.getPageManager();
				const currentPage = pageManager.currentPage();
				const datasetPageMap = pageManager.getDatasetNameMap();

				// only include datasets associated with the current file and page
				datasets = fileDatasets.filter(
					(d) => datasetPageMap[d.name] === currentPage,
				);
			}

			// loop through datasets associated with the current file
			for (let dataset of datasets) {
				// collect image axes that are table axes
				const axis = plotData.getAxesForDataset(dataset);

				if (axis instanceof wpd.ImageAxes && axis.getMetadata().table) {
					axisList.push(axis);
					datasetList.push(dataset);
				}
			}

			// generate table tree
			const tableTreeContainer = wpdDocument.getElementsByClassName(
				"tree-widget-tables-list",
			)[0];

			// clone and replace the container to remove all previous listeners
			const clone = tableTreeContainer.cloneNode();
			tableTreeContainer.parentNode.replaceChild(clone, tableTreeContainer);

			axisTree = new wpd.TreeWidget(clone);
			const tableAxes = [...new Set(axisList)];

			// setup table tree event handlers
			axisTree.onItemSelection((el, path) => {
				// unselect all data points
				activeDataset?.unselectAll();

				// remove the leading slash and unescape slashes
				const name = el.getAttribute("data-axis-name");

				// find the axis
				const tableAxis = tableAxes.find((axis) => axis.name === name);

				if (tableAxis) {
					wpd.tree.setActiveAxis(tableAxis);

					wpd.alignAxes.backfillTableTagGroups(tableAxis);

					const isEmpty = wpd.tableAxis.areTableHeadersEmpty();
					const isValid = wpd.tableAxis.validateTableHeaders();

					const calibrationComplete =
						tableAxis.getMetadata()?.calibration?.calibrationComplete;
					// check if table has valid headers
					if (isEmpty || !isValid || !calibrationComplete) {
						wpd.events.dispatch("wpd.axes.select", {
							axes: tableAxis,
							el,
						});
					} else {
						// find the first dataset associated with the current axis (table)
						const firstDataset = datasets.find(
							(d) => plotData.getAxesForDataset(d) === tableAxis,
						);

						// select the first dataset associated with the current file
						wpd.tree.setActiveDataset(firstDataset);

						wpd.events.dispatch("wpd.dataset.select", {
							dataset: firstDataset,
						});
					}
				}
			});

			// render the table tree
			axisTree.render(
				tableAxes.map((axis) => {
					return {
						[axis.name]: {
							title: `${wpd.custom.identifyAxis(axis)} axis`,
							type: `${wpd.custom.identifyAxis(axis)}-axis`,
							data: [
								{
									key: "axis-name",
									value: axis.name,
								},
							],
							children: [],
						},
					};
				}),
				null,
				"table-axis-",
			);

			// set active axis to null
			wpd.tree.setActiveAxis(null);

			// unset active point group
			wpd.tree.setActivePointGroup(null);

			// ensure tag group controls are hidden
			wpd.tagGroups.hideControls();

			// paint all the table columns
			wpd.graphicsWidget.setRepainter(
				new wpd.MultipltDatasetRepainter(axisList, datasetList),
			);
		}

		function editDataset(datasetName) {
			// switch to the given dataset
			switchDataset(datasetName);

			wpd.dataSeriesManagement.showEditDataset();
		}

		function renameDataset(datasetName) {
			// switch to the given dataset
			switchDataset(datasetName);

			wpd.dataSeriesManagement.showRenameDataset();
		}

		function renameAxis(axisName) {
			// switch to the given axis
			_switchAxis(axisName);

			const axis = wpd.appData
				.getPlotData()
				.getAxesColl()
				.find((a) => a.name === axisName);

			const axisType = wpd.custom.identifyAxis(axis);

			const renameAxesPopup = wpdDocument.getElementById("rename-axes-popup");
			const heading = renameAxesPopup.getElementsByClassName("popupheading")[0];

			if (["xy", "bar"].includes(axisType)) {
				heading.innerText = "Rename Graph";
			} else if (axisType === "table") {
				heading.innerText = "Rename Table";
			} else {
				heading.innerText = "Rename Object";
			}

			wpd.alignAxes.showRenameAxes();
		}

		function selectAxis(axisName, el) {
			// switch to the given axis
			const axis = _switchAxis(axisName);
			wpd.events.dispatch("wpd.axes.select", {
				axes: axis,
				el,
			});

			return axis;
		}

		async function deleteAxis(axisName, skipConfirmation) {
			if (skipConfirmation) {
				await this.confirmDeleteAxis(axisName);
			} else {
				_switchAxis(axisName);
				const popup = wpdDocument.getElementById("delete-axis-confirm-dialog");
				popup.dataset.axisName = axisName;
				popup.querySelector(".axis-name").innerText = axisName;
				wpd.popup.show("delete-axis-confirm-dialog");
			}
		}

		async function confirmDeleteAxis(axisName) {
			const popup = wpdDocument.getElementById("delete-axis-confirm-dialog");
			const tableAxisName = axisName ?? popup.dataset.axisName;
			const axis = _switchAxis(tableAxisName);

			// delete axis and associated datasets
			wpd.custom.deleteAxisDeep(axis);

			wpd.tree.refreshPreservingSelection();

			// clean up the canvas
			wpd.graphicsWidget.removeRepainter();
			wpd.graphicsWidget.removeTool();
			wpd.graphicsWidget.resetData();

			wpd.sidebar.clear();

			wpd.popup.close("delete-axis-confirm-dialog");
			await dataEntryVM.autoSaveMetadata();
		}

		function cancelDeleteAxis() {
			wpd.popup.close("delete-axis-confirm-dialog");
		}

		function deleteDataset(datasetName) {
			const dataset = switchDataset(datasetName);

			// delete dataset
			wpd.custom.deleteDatasetDeep(dataset);

			wpd.tree.refreshPreservingSelection();
		}

		async function addNewStudy(el) {
			wpd.tagGroups.setActiveKeys({});
			prepareTextSelection();
			await newTagGroup(el, wpd.tagGroups.studyTagGroupName);
		}

		async function addNewStudyPart(el, e) {
			const closestFolder = await selectClosestFolder(el);
			const compositeKey = closestFolder.dataset.compositeKey;
			const parsedKey = wpd.CompositeKeyUtils.parseCompositeKey(compositeKey);
			wpd.tagGroups.setActiveKeys(parsedKey);
			await newTagGroup(el, "Study part");
		}

		async function addNewStudyArm(el, e) {
			await _addNewTreeBranch(el, wpd.tagGroups.studyArmTagGroupName);
		}

		async function addNewPopulation(el, e) {
			await _addNewTreeBranch(el, wpd.tagGroups.studyPopulationTagGroupName);
		}

		async function addNewSubgroup(el, e) {
			await _addNewTreeBranch(el, wpd.tagGroups.studySubgroupTagGroupName);
		}

		async function _addNewTreeBranch(el, tagGroupName) {
			const closestFolder = await selectClosestFolder(el);
			const compositeKey = closestFolder.dataset.compositeKey;
			const parsedKey = wpd.CompositeKeyUtils.parseCompositeKey(compositeKey);
			wpd.tagGroups.setActiveKeys(parsedKey);
			await newTagGroup(el, tagGroupName);
		}

		async function addStudyDataPoint(el, e) {
			await _addStudyBranchDataPoint(el, wpd.tagGroups.studyTagGroupName);
		}

		async function addStudyPartDataPoint(el, e) {
			await _addStudyBranchDataPoint(el, wpd.tagGroups.studyPartTagGroupName);
		}

		async function addStudyArmDataPoint(el, e) {
			await _addStudyBranchDataPoint(el, wpd.tagGroups.studyArmTagGroupName);
		}

		async function addStudyPopulationDataPoint(el, e) {
			await _addStudyBranchDataPoint(
				el,
				wpd.tagGroups.studyPopulationTagGroupName,
			);
		}

		async function addStudySubgroupDataPoint(el, e) {
			await _addStudyBranchDataPoint(
				el,
				wpd.tagGroups.studySubgroupTagGroupName,
			);
		}

		async function _addStudyBranchDataPoint(el, tagGroupName) {
			const closestFolder = await selectClosestFolder(el);
			const compositeKey = closestFolder.dataset.compositeKey;
			const existing = findCompositeKeyMatch(compositeKey, tagGroupName);
			const parsedKey = wpd.CompositeKeyUtils.parseCompositeKey(compositeKey);
			wpd.tagGroups.setActiveKeys(parsedKey);
			if (existing) {
				wpd.tagGroups.loadTagGroupInstance(existing.metadata.tagGroup);
			} else {
				wpd.tagGroups.newTagGroupInstance(
					wpd.tagGroups.tagGroups.find((group) => group.name === tagGroupName),
				);
			}
		}

		async function editAbbreviation(el, e) {
			const closestFolder = await selectClosestFolder(el);
			const compositeKey = closestFolder.dataset.compositeKey;
			const parsedKey = wpd.CompositeKeyUtils.parseCompositeKey(compositeKey);
			wpd.tagGroups.setActiveKeys(parsedKey);

			const existing = findCompositeKeyMatch(compositeKey);
			if (existing) {
				wpd.tagGroups.loadTagGroupInstance(existing.metadata.tagGroup);
				await wpd.tagGroups.selectTag(el, true);
			} else {
				wpd.popup.show("edit-non-existent-abbreviation-dialog");
			}
		}

		async function showDeleteStudyElementPopup(el, e) {
			const closestFolder = await selectClosestFolder(el);
			const compositeKey = closestFolder.dataset.compositeKey;
			const existing = findCompositeKeyMatch(compositeKey);
			if (existing) {
				wpd.popup.show("delete-study-element-confirm-dialog");
			} else {
				wpd.popup.show("delete-study-element-empty-dialog");
			}
		}

		function closeDeleteStudyElementPopup() {
			wpd.popup.close("delete-study-element-confirm-dialog");
		}

		function closeDeleteStudyElementErrorPopup() {
			wpd.popup.close("delete-study-element-error-dialog");
			const errorDialog = wpdDocument.getElementById(
				"delete-study-element-error-dialog",
			);

			const tableReferences = errorDialog.getElementsByClassName(
				"table-reference-container",
			)[0];
			const tableListContainer = tableReferences.getElementsByTagName("ul")[0];
			tableListContainer.innerHTML = "";

			const graphReferences = errorDialog.getElementsByClassName(
				"graph-reference-container",
			)[0];
			const graphListContainer = graphReferences.getElementsByTagName("ul")[0];
			graphListContainer.innerHTML = "";
		}

		function closeDeleteStudyElementEmptyPopup() {
			wpd.popup.close("delete-study-element-empty-dialog");
		}

		function closeEditNonExistentElementPopup() {
			wpd.popup.close("edit-non-existent-abbreviation-dialog");
		}

		async function deleteStudyElement() {
			const studyContainer = wpdDocument.getElementById("study-tree-display");
			const selected =
				studyContainer.getElementsByClassName("tree-selected")[0];

			if (!selected) {
				return;
			}

			const childFolders = selected
				.closest("li")
				.getElementsByClassName("tree-folder");

			const sortedFolders = Array.from(childFolders).reverse();
			const datasets = wpd.appData.getPlotData().getDatasets();

			let hasTableReferences = false;
			let hasGraphReferences = false;
			for (const folder of sortedFolders) {
				const compositeKey = wpdDocument.getElementById(folder.id).dataset
					.compositeKey;

				const references = studyBranchCalibrationReferences(compositeKey);
				if (references.tables.length > 0 || references.graphs.length > 0) {
					closeDeleteStudyElementPopup();
					const errorDialog = wpdDocument.getElementById(
						"delete-study-element-error-dialog",
					);

					const tableReferences = errorDialog.getElementsByClassName(
						"table-reference-container",
					)[0];
					if (references.tables.length > 0) {
						hasTableReferences = true;
						tableReferences.classList.remove("hidden");
						const list = tableReferences.getElementsByTagName("ul")[0];
						list.insertAdjacentHTML(
							"beforeend",
							references.tables
								.map(
									(t) =>
										`<li><span style="font-weight: bold">${t}</span> (${folder.innerHTML.trim()})</li>`,
								)
								.join(""),
						);
					} else if (!hasTableReferences) {
						tableReferences.classList.add("hidden");
					}

					const graphReferences = errorDialog.getElementsByClassName(
						"graph-reference-container",
					)[0];
					if (references.graphs.length > 0) {
						hasGraphReferences = true;
						graphReferences.classList.remove("hidden");
						const list = graphReferences.getElementsByTagName("ul")[0];
						list.insertAdjacentHTML(
							"beforeend",
							references.graphs
								.map(
									(g) =>
										`<li><span style="font-weight: bold">${g}</span> (${folder.innerHTML.trim()})</li>`,
								)
								.join(""),
						);
					} else if (!hasGraphReferences) {
						graphReferences.classList.add("hidden");
					}

					wpd.popup.show("delete-study-element-error-dialog");
				}
			}

			if (hasTableReferences || hasGraphReferences) {
				return;
			}

			for (const folder of sortedFolders) {
				prepareTextSelection();
				const compositeKey = wpdDocument.getElementById(folder.id).dataset
					.compositeKey;

				datasets.forEach((ds) => {
					const match = findCompositeKeyMatch(compositeKey, null, ds);
					if (match) {
						wpd.tagGroups.loadTagGroupInstance(match.metadata.tagGroup);
						wpd.tagGroups.clearTags(
							true,
							ds,
							match.metadata.tagGroup.instanceUuid,
						);
					}
				});
			}

			await dataEntryVM.autoSaveMetadata();

			activeDataset?.unselectAll();
			wpd.tagGroups.reset();
			closeDeleteStudyElementPopup();
		}

		function studyBranchCalibrationReferences(compositeKey) {
			const axes = wpd.appData.getPlotData().getAxesColl();
			const references = { tables: [], graphs: [] };
			axes.forEach((axis) => {
				const axisMetadata = axis.getMetadata();
				const axisType = wpd.custom.identifyAxis(axis);

				// tables
				if (axisType === "table" && axisMetadata.calibration) {
					const rows = axisMetadata.calibration.rows?.headers;
					const cols = axisMetadata.calibration.cols?.headers;
					[...(rows ?? []), ...(cols ?? [])].forEach((header) => {
						if (header) {
							if (header.compositeKey === compositeKey) {
								references.tables.push(axis.name);
							}
						}
					});
				}
				// graphs
				else if (["xy", "bar"].includes(axisType)) {
					const datasets = wpd.appData.getPlotData().getDatasetsForAxis(axis);

					datasets.forEach((ds) => {
						const dsMetadata = ds.getMetadata();
						if (dsMetadata.studyData.compositeKey === compositeKey) {
							references.graphs.push(axis.name);
						}
					});
				}
			});
			return references;
		}

		async function selectClosestFolder(el) {
			const closestFolder = el
				.closest(".tree-list-content")
				.getElementsByClassName("tree-folder")[0];
			const isSelected = closestFolder.classList.contains("tree-selected");

			if (!isSelected) {
				await wpd.tagGroups.studyTree._onclick(closestFolder);
			}

			prepareTextSelection();
			return closestFolder;
		}

		function findCompositeKeyMatch(compositeKey, tagGroupName, dataset) {
			const ds = dataset ? dataset : activeDataset;
			return ds
				?.getAllPixels()
				.find(
					(pixel) =>
						(!tagGroupName || pixel.metadata.tagGroup.name === tagGroupName) &&
						pixel.metadata.compositeKey === compositeKey,
				);
		}

		async function newTagGroup(el, tagGroupName) {
			const tagGroup = wpd.tagGroups.tagGroups.find(
				(group) => group.name === tagGroupName,
			);
			wpd.tagGroups.newTagGroupInstance(tagGroup);
			await wpd.tagGroups.selectTag(el, true);
		}

		function prepareTextSelection() {
			showTreeItemWidget("text-item-tree-widget");
			onTextSelection();
		}

		function editXYLabels(axisName) {
			// switch to the given axis
			_switchAxis(axisName);

			const plotData = wpd.appData.getPlotData();

			// get the label axis for the given axis name
			const xyLabelAxis = plotData
				.getAxesColl()
				.find((a) => a.name === `${axisName} Labels`);

			// find the label dataset associated with this axis
			const xyLabelDataset = plotData.getDatasets().find((d) => {
				return (
					plotData.getAxesForDataset(d) === xyLabelAxis &&
					d.name.startsWith(`${axisName} Labels`)
				);
			});

			// load the xy label dataset
			onDatasetSelection(xyLabelDataset.name);
		}

		function editAxis(axisName, el) {
			// switch to the given axis
			const axis = selectAxis(axisName, el);

			// image and table axes are both based on the image axis class
			// ignore image and table axes
			const axisType = wpd.custom.identifyAxis(axis);

			const excludeList = ["image", "xy", "bar"];
			if (!excludeList.includes(axisType)) {
				// trigger calibration
				wpd.alignAxes.reloadCalibrationForEditing();
			}
		}

		function editPointGroups(datasetName) {
			switchDataset(datasetName);

			wpd.pointGroups.showSettingsPopup();
		}

		function switchDataset(datasetName) {
			// unselect all data points
			activeDataset?.unselectAll();

			// find the named dataset in the collection
			const dataset = wpd.appData
				.getPlotData()
				.getDatasets()
				.find((d) => d.name === datasetName);

			// set the active axis
			wpd.tree.setActiveAxis(
				wpd.appData.getPlotData().getAxesForDataset(dataset),
			);

			// select the dataset
			if (axisTree) {
				if (activeAxis.getMetadata().table) {
					axisTree.selectPath(`/${activeAxis.name}`);
				} else {
					axisTree.selectPath(`/${activeAxis.name}/${datasetName}`);
				}
			}

			// set as active axis
			wpd.tree.setActiveDataset(dataset);

			return dataset;
		}

		function _switchAxis(axisName) {
			// unselect all data points
			activeDataset?.unselectAll();

			// select the axis
			axisTree.selectPath(`/${axisName}`);

			// find the named axis in the collection
			const axis = wpd.appData
				.getPlotData()
				.getAxesColl()
				.find((a) => a.name === axisName);

			// set as active axis
			wpd.tree.setActiveAxis(axis);

			return axis;
		}

		function selectDataset(datasetName) {
			// unselect all data points
			activeDataset?.unselectAll();
			onDatasetSelection(datasetName);
		}

		function addGraphAxis(el) {
			wizard = true;

			// update button appearance
			el.classList.add("pressed-button");

			// unset active axis and dataset
			wpd.tree.setActiveAxis(null);
			wpd.tree.setActiveDataset(null);

			// unselect axis tree items
			axisTree._unselectAll();

			// trigger table calibration tool
			wpd.alignAxes.addCalibration();
		}

		function addTableAxis(el) {
			// update button appearance
			el.classList.add("pressed-button");

			// unset active axis and dataset
			wpd.tree.setActiveAxis(null);
			wpd.tree.setActiveDataset(null);
			wpd.alignAxes.resetTableCalibration();

			// unselect axis tree items
			axisTree._unselectAll();

			wpd.tree.selectPath("/Table");

			// trigger table calibration tool
			wpd.alignAxes.pickArea();

			const calibrationNextButton = wpdDocument.getElementById(
				"table-area-calibration-next-button",
			);
			calibrationNextButton.disabled = true;
		}

		function onSelection(el, path, suppressSecondaryActions, preserveActive) {
			// unselect all data points
			activeDataset?.unselectAll();

			switch (true) {
				case path === `/${mode.text}`:
					showTreeItemWidget("text-item-tree-widget");
					onTextSelection();
					break;
				case path === `/${mode.graph}`:
					showTreeItemWidget("graph-item-tree-widget");
					onGraphSelection(preserveActive);
					break;
				case path === `/${mode.table}`:
					showTreeItemWidget("table-item-tree-widget");
					onTableSelection();
					break;
				default:
					resetGraphics();
					showTreeItemWidget(null);
					wpd.tree.setActiveAxis(null);
			}
		}

		function onRename(el, path) {
			if (!path) return;
			if (path.startsWith("/" + wpd.gettext("datasets") + "/")) {
				wpd.dataSeriesManagement.showRenameDataset();
			} else if (path.startsWith("/" + wpd.gettext("axes") + "/")) {
				wpd.alignAxes.showRenameAxes();
			}
		}

		function init() {
			const container = wpdDocument.getElementById("tree-container");
			rootTree = new wpd.TreeWidget(container);
			rootTree.onItemSelection(onSelection);
			rootTree.onItemRename(onRename);
			buildTree();
		}

		function refresh() {
			buildTree();
		}

		function completeTableCalibration() {
			const isEmpty = wpd.tableAxis.areTableHeadersEmpty();
			const isValid = wpd.tableAxis.validateTableHeaders();
			const hasEmptyHeaders = wpd.tableAxis.hasEmptyTableHeaders();

			if (isEmpty) {
				wpd.tableAxis.showEmptyTableHeadersDialog();
			} else if (!isValid) {
				wpd.tableAxis.showInvalidTableHeadersDialog();
			} else if (hasEmptyHeaders) {
				wpd.tableAxis.showHasEmptyTableHeadersDialog();
			} else {
				const activeAxes = wpd.tree.getActiveAxes();
				const metadata = activeAxes.getMetadata();
				metadata.calibration.calibrationComplete = true;
				activeAxes.setMetadata(metadata);
				wpd.tree.refreshPreservingSelection();
			}
		}

		function refreshPreservingSelection(forceRefresh) {
			if (rootTree != null) {
				// cache existing selection
				const selectedPath = rootTree.getSelectedPath();

				// handle axis tree selection
				let selectedAxis;
				if (axisTree) {
					selectedAxis = axisTree.getSelectedPath();
				}

				// do the refresh
				refresh();

				// re-apply cached selections
				rootTree.selectPath(selectedPath, !forceRefresh, true);

				if (axisTree) {
					axisTree.selectPath(selectedAxis, !forceRefresh, true);
				}
			} else {
				refresh();
			}
		}

		function getAxisTreeSelectedPath() {
			if (axisTree) {
				return axisTree.getSelectedPath();
			}

			return null;
		}

		function selectPath(path, suppressSecondaryActions) {
			// unselect all data points
			activeDataset?.unselectAll();

			// handle dataset selection here, this will come from table cell selection tool
			if (path === null) {
				// Unselect everything
				rootTree._unselectAll();

				// clear tag group ui elements
				wpd.tagGroups.hideControls();

				// display everything on this file (and page)
				onDatasetGroupSelection();
			} else {
				rootTree.selectPath(path, suppressSecondaryActions);
			}
		}

		function selectAxisPath(path, suppressSecondaryActions) {
			// unselect all data points
			activeDataset?.unselectAll();

			// handle dataset selection here, this will come from table cell selection tool
			if (path === null) {
				// Unselect everything
				axisTree._unselectAll();

				// clear tag group ui elements
				wpd.tagGroups.hideControls();

				// display everything on this file (and page)
				onDatasetGroupSelection();
			} else {
				axisTree.selectPath(path, suppressSecondaryActions);
			}
		}

		function addMeasurement(mode) {
			wpd.measurement.start(mode);
			refresh();
			let suppressSecondaryActions = true;
			if (wpd.appData.isMultipage()) suppressSecondaryActions = false;
			if (mode === wpd.measurementModes.distance) {
				wpd.tree.selectPath(
					"/" + wpd.gettext("measurements") + "/" + wpd.gettext("distance"),
					suppressSecondaryActions,
				);
			} else if (mode === wpd.measurementModes.angle) {
				wpd.tree.selectPath(
					"/" + wpd.gettext("measurements") + "/" + wpd.gettext("angle"),
					suppressSecondaryActions,
				);
			} else if (mode === wpd.measurementModes.area) {
				wpd.tree.selectPath(
					"/" + wpd.gettext("measurements") + "/" + wpd.gettext("area"),
					suppressSecondaryActions,
				);
			}
		}

		function getActiveDataset() {
			return activeDataset;
		}

		function getActiveAxes() {
			return activeAxis;
		}

		function getAxisTree() {
			return axisTree;
		}

		function setActiveDataset(dataset) {
			activeDataset = dataset;
		}

		function setActiveAxis(axis) {
			activeAxis = axis;
		}

		function inWizard() {
			return wizard;
		}

		function exitWizard() {
			wizard = false;
		}

		function getWizardDatasets() {
			return wizardDatasets;
		}

		function setWizardDatasets(datasets) {
			wizardDatasets = datasets;
		}

		return {
			addGraphAxis: addGraphAxis,
			addMeasurement: addMeasurement,
			addNewStudy: addNewStudy,
			addNewStudyArm: addNewStudyArm,
			addNewStudyPart: addNewStudyPart,
			addNewPopulation: addNewPopulation,
			addNewSubgroup: addNewSubgroup,
			addStudyDataPoint: addStudyDataPoint,
			addStudyArmDataPoint: addStudyArmDataPoint,
			addStudyPartDataPoint: addStudyPartDataPoint,
			addStudyPopulationDataPoint: addStudyPopulationDataPoint,
			addStudySubgroupDataPoint: addStudySubgroupDataPoint,
			addTableAxis: addTableAxis,
			cancelDeleteAxis: cancelDeleteAxis,
			closeDeleteStudyElementPopup: closeDeleteStudyElementPopup,
			closeDeleteStudyElementErrorPopup: closeDeleteStudyElementErrorPopup,
			closeDeleteStudyElementEmptyPopup: closeDeleteStudyElementEmptyPopup,
			closeEditNonExistentElementPopup: closeEditNonExistentElementPopup,
			completeTableCalibration: completeTableCalibration,
			confirmDeleteAxis: confirmDeleteAxis,
			deleteAxis: deleteAxis,
			deleteDataset: deleteDataset,
			deleteStudyElement: deleteStudyElement,
			editAxis: editAxis,
			editDataset: editDataset,
			editPointGroups: editPointGroups,
			editAbbreviation: editAbbreviation,
			editXYLabels: editXYLabels,
			exitWizard: exitWizard,
			findCompositeKeyMatch: findCompositeKeyMatch,
			getActiveAxes: getActiveAxes,
			getActiveDataset: getActiveDataset,
			getActivePointGroup: getActivePointGroup,
			getAxisTree: getAxisTree,
			getAxisTreeSelectedPath: getAxisTreeSelectedPath,
			getRootTreeSelectedPath: getRootTreeSelectedPath,
			getWizardDatasets: getWizardDatasets,
			graphPath: graphPath,
			init: init,
			inWizard: inWizard,
			onTableSelection: onTableSelection,
			onTextSelection: onTextSelection,
			refresh: refresh,
			refreshPreservingSelection: refreshPreservingSelection,
			renameAxis: renameAxis,
			renameDataset: renameDataset,
			selectAxis: selectAxis,
			selectDataset: selectDataset,
			selectPath: selectPath,
			selectAxisPath: selectAxisPath,
			setActiveAxis: setActiveAxis,
			setActiveDataset: setActiveDataset,
			setActivePointGroup: setActivePointGroup,
			setWizardDatasets: setWizardDatasets,
			showDeleteStudyElementPopup: showDeleteStudyElementPopup,
			showTreeItemWidget: showTreeItemWidget,
			switchDataset: switchDataset,
			tablePath: tablePath,
			textPath: textPath,
		};
	})();
}

export { init };
